/**
 * SENI Theme - Cart Functionality
 * Handles cart drawer, AJAX cart updates, and add-to-cart actions
 */

(function() {
  'use strict';

  // DOM Elements
  let cartDrawer;
  let cartDrawerOverlay;
  let cartDrawerBody;

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', initCart);

  function initCart() {
    cartDrawer = document.querySelector('[data-cart-drawer]');
    cartDrawerOverlay = document.querySelector('[data-cart-drawer-overlay]');
    cartDrawerBody = document.querySelector('[data-cart-drawer-body]');

    // Setup event listeners
    setupDrawerControls();
    setupQuantityControls();
    setupAddToCartForms();
    setupQuickAdd();
  }

  // ============================================
  // Cart Drawer Controls
  // ============================================
  
  window.openCartDrawer = function() {
    if (!cartDrawer) {
      cartDrawer = document.querySelector('[data-cart-drawer]');
    }
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus first interactive element
      const closeBtn = cartDrawer.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }
  };

  window.closeCartDrawer = function() {
    if (!cartDrawer) {
      cartDrawer = document.querySelector('[data-cart-drawer]');
    }
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  };

  function setupDrawerControls() {
    // Overlay click to close
    if (cartDrawerOverlay) {
      cartDrawerOverlay.addEventListener('click', window.closeCartDrawer);
    }

    // Close buttons
    document.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
      btn.addEventListener('click', window.closeCartDrawer);
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer?.getAttribute('aria-hidden') === 'false') {
        window.closeCartDrawer();
      }
    });

    // Cart toggle buttons
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        window.openCartDrawer();
      });
    });

    // Clear cart button
    document.querySelectorAll('[data-clear-cart]').forEach(btn => {
      btn.addEventListener('click', clearCart);
    });
  }

  function clearCart() {
    // Use translation if available, fallback to English
    const confirmMsg = window.SeniTranslations?.cart_clear_confirm || 'Are you sure you want to clear your cart?';
    if (!confirm(confirmMsg)) {
      return;
    }

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(() => {
      refreshCartDrawer();
      updateCartCount(0);
    })
    .catch(error => {
      console.error('Clear cart error:', error);
    });
  }

  // ============================================
  // Quantity Controls
  // ============================================

  function setupQuantityControls() {
    document.querySelectorAll('[data-cart-item]').forEach(item => {
      const key = item.dataset.lineItemKey;
      const input = item.querySelector('[data-quantity-input]');
      const minusBtn = item.querySelector('[data-quantity-minus]');
      const plusBtn = item.querySelector('[data-quantity-plus]');
      const removeBtn = item.querySelector('[data-remove-item]');

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          const currentQty = parseInt(input?.value) || 0;
          updateCartItem(key, Math.max(0, currentQty - 1), item);
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          const currentQty = parseInt(input?.value) || 0;
          updateCartItem(key, currentQty + 1, item);
        });
      }

      if (input) {
        input.addEventListener('change', () => {
          updateCartItem(key, parseInt(input.value) || 0, item);
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          updateCartItem(key, 0, item);
        });
      }
    });
  }

  // ============================================
  // AJAX Cart Operations
  // ============================================

  function updateCartItem(key, quantity, itemElement) {
    // Show loading state
    if (itemElement) {
      itemElement.style.opacity = '0.5';
      itemElement.style.pointerEvents = 'none';
    }

    // If removing item (quantity = 0), check if it has linked Seni fee product
    if (quantity === 0) {
      removeItemWithLinkedFee(key, itemElement);
      return;
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    })
    .then(response => response.json())
    .then(cart => {
      refreshCartDrawer();
      updateCartCount(cart);
    })
    .catch(error => {
      console.error('Cart update error:', error);
      if (itemElement) {
        itemElement.style.opacity = '1';
        itemElement.style.pointerEvents = '';
      }
    });
  }

  // Remove item and any linked Seni fee product
  function removeItemWithLinkedFee(key, itemElement) {
    // First, get current cart to find any linked fee product
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Find the item being removed
        const itemToRemove = cart.items.find(item => item.key === key);
        
        if (!itemToRemove) {
          // Item not found, just try to remove it
          return removeItems([key]);
        }

        // Check if this item has a Seni customization ID
        const customizationId = itemToRemove.properties && itemToRemove.properties['_seni_customization_id'];
        
        if (customizationId) {
          // Find the linked fee product with same customization ID
          const linkedFeeItem = cart.items.find(item => 
            item.properties && 
            item.properties['_seni_fee'] === 'true' &&
            item.properties['_seni_customization_id'] === customizationId
          );
          
          if (linkedFeeItem) {
            // Remove both the main item and the fee item
            console.log('Seni: Removing main product and linked fee product');
            return removeItems([key, linkedFeeItem.key]);
          }
        }
        
        // No linked fee product, just remove this item
        return removeItems([key]);
      })
      .then(() => {
        refreshCartDrawer();
        return fetch('/cart.js');
      })
      .then(response => response.json())
      .then(cart => {
        updateCartCount(cart);
      })
      .catch(error => {
        console.error('Cart removal error:', error);
        if (itemElement) {
          itemElement.style.opacity = '1';
          itemElement.style.pointerEvents = '';
        }
      });
  }

  // Remove multiple items from cart
  function removeItems(keys) {
    const updates = {};
    keys.forEach(key => {
      updates[key] = 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates: updates })
    }).then(response => response.json());
  }

  function refreshCartDrawer() {
    // Use Shopify routes root for proper locale/market support
    const rootUrl = window.Shopify?.routes?.root || '/';
    
    // Fetch updated cart drawer HTML using section rendering API
    fetch(`${rootUrl}?sections=cart-drawer`)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawer = doc.querySelector('[data-cart-drawer]');
        
        if (newDrawer && cartDrawer) {
          // Preserve open state
          const wasOpen = cartDrawer.getAttribute('aria-hidden') === 'false';
          cartDrawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen) {
            cartDrawer.setAttribute('aria-hidden', 'false');
          }
          // Re-bind quantity controls after DOM update
          setupQuantityControls();
          setupDrawerControls();
        }
      })
      .catch(error => {
        console.error('Failed to refresh cart drawer:', error);
        // Don't reload on error - just log it
      });
  }

  function calculateVisibleCount(cart) {
    // Exclude Seni fee products from count (products with _seni_fee property)
    if (!cart || !cart.items) return 0;
    
    return cart.items.reduce((count, item) => {
      // Check if item has _seni_fee property
      const isFeeProduct = item.properties && item.properties['_seni_fee'];
      if (!isFeeProduct) {
        count += item.quantity;
      }
      return count;
    }, 0);
  }

  function updateCartCount(cartOrCount) {
    // Accept either a count number or a cart object
    const count = typeof cartOrCount === 'number' 
      ? cartOrCount 
      : calculateVisibleCount(cartOrCount);
    
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  // ============================================
  // Add to Cart Handling
  // ============================================

  function setupAddToCartForms() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      form.addEventListener('submit', handleAddToCart);
    });
  }

  function handleAddToCart(e) {
    const form = e.target;
    
    console.log('Cart.js: handleAddToCart triggered');
    console.log('Cart.js: defaultPrevented =', e.defaultPrevented);
    console.log('Cart.js: seniHandled =', form.dataset.seniHandled);
    
    // If Seni is handling this form, skip entirely
    if (form.dataset.seniHandled === 'true') {
      console.log('Cart.js: Seni handled this, skipping');
      form.dataset.seniHandled = ''; // Reset for next submission
      return;
    }
    
    // If event was already handled (by Seni or other), skip
    if (e.defaultPrevented) {
      console.log('Cart.js: Event already handled, skipping');
      return;
    }
    
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;

    // Show loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding...';
    }

    const formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(item => {
      // Update cart count
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          updateCartCount(cart);
          refreshCartDrawer();
          window.openCartDrawer();
        });
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      alert('Could not add item to cart. Please try again.');
    })
    .finally(() => {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    });
  }

  // ============================================
  // Quick Add from Product Cards
  // ============================================

  function setupQuickAdd() {
    // Use event delegation for all quick add interactions
    // This ensures it works with dynamically loaded product cards
    document.addEventListener('click', handleQuickAddClick);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAllQuickAddPopups();
      }
    });
  }

  function handleQuickAddClick(e) {
    const target = e.target;

    // Direct add button (single variant products)
    const quickAddBtn = target.closest('[data-quick-add]');
    if (quickAddBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleQuickAddDirect(quickAddBtn);
      return;
    }

    // Popup trigger button (multi-variant products)
    const popupBtn = target.closest('[data-quick-add-popup]');
    if (popupBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleQuickAddPopupToggle(popupBtn);
      return;
    }

    // Close popup button
    const closeBtn = target.closest('[data-quick-add-close]');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      closeAllQuickAddPopups();
      return;
    }

    // Variant selection button
    const variantBtn = target.closest('[data-variant-select]');
    if (variantBtn && !variantBtn.disabled) {
      e.preventDefault();
      e.stopPropagation();
      handleVariantSelect(variantBtn);
      return;
    }

    // Close popups when clicking outside product cards
    if (!target.closest('[data-product-card]') && !target.closest('[data-quick-add-panel]')) {
      closeAllQuickAddPopups();
    }
  }

  function handleQuickAddDirect(btn) {
    const variantId = btn.dataset.variantId;

    if (!variantId) return;

    // Show loading state
    btn.classList.add('product-card__add-btn--loading');
    btn.disabled = true;

    addToCart(variantId, 1)
      .then(() => {
        // Success feedback
        const textEl = btn.querySelector('.product-card__add-btn-text');
        if (textEl) {
          const originalText = textEl.textContent;
          textEl.textContent = '✓';
          setTimeout(() => {
            textEl.textContent = originalText;
          }, 1500);
        }
      })
      .catch(error => {
        console.error('Quick add error:', error);
        alert(window.SeniTranslations?.addError || 'Could not add to cart. Please try again.');
      })
      .finally(() => {
        btn.classList.remove('product-card__add-btn--loading');
        btn.disabled = false;
      });
  }

  function handleQuickAddPopupToggle(btn) {
    const quickAddContainer = btn.closest('.product-card__quick-add');
    const panel = quickAddContainer?.querySelector('[data-quick-add-panel]');

    if (!panel) return;

    const isOpen = panel.getAttribute('aria-hidden') === 'false';

    // Close all other popups first
    closeAllQuickAddPopups();

    if (!isOpen) {
      panel.setAttribute('aria-hidden', 'false');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  function closeAllQuickAddPopups() {
    document.querySelectorAll('[data-quick-add-panel]').forEach(panel => {
      panel.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('[data-quick-add-popup]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  function handleVariantSelect(btn) {
    const variantId = btn.dataset.variantId;
    const panel = btn.closest('[data-quick-add-panel]');
    const loadingEl = panel?.querySelector('[data-popup-loading]');
    const optionsEl = panel?.querySelector('[data-variant-options]');

    if (!variantId) return;

    // Show loading state
    if (loadingEl) loadingEl.hidden = false;
    if (optionsEl) optionsEl.style.opacity = '0.5';
    btn.disabled = true;

    addToCart(variantId, 1)
      .then(() => {
        // Close popup on success
        closeAllQuickAddPopups();
      })
      .catch(error => {
        console.error('Quick add variant error:', error);
        alert(window.SeniTranslations?.addError || 'Could not add to cart. Please try again.');
      })
      .finally(() => {
        if (loadingEl) loadingEl.hidden = true;
        if (optionsEl) optionsEl.style.opacity = '1';
        btn.disabled = false;
      });
  }

  function addToCart(variantId, quantity = 1) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Add to cart failed');
      }
      return response.json();
    })
    .then(item => {
      // Update cart and open drawer
      return fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          updateCartCount(cart);
          refreshCartDrawer();
          window.openCartDrawer();
          return item;
        });
    });
  }

  // Expose for external use
  window.SENICart = {
    open: window.openCartDrawer,
    close: window.closeCartDrawer,
    refresh: refreshCartDrawer,
    updateCount: updateCartCount,
    addToCart: addToCart
  };

})();

